import { BigNumber } from 'bignumber.js'
import { match, P } from 'ts-pattern'

import { Duration, Order } from '@blade/std-lib'
import { CurrencyModel } from '@root/entities/currency/model'
import { SuggestedGasSettings } from '@root/entities/gas-information'
import { Network } from '@root/entities/network'

import type { QuoteResponseBody } from '@blade/pathfinder-api-client'
import type { ServicesApi } from '@root/shared/api'

export type QuoteType = 'EXACT_INPUT' | 'EXACT_OUTPUT'

export interface FeeConfig
  extends NonNullable<ServicesApi.QuoteDto['feeConfig']> {}

export interface FeeData extends NonNullable<ServicesApi.QuoteDto['fee']> {}

interface QuoteCommons {
  minimumReceive: BigNumber

  inputAmount: BigNumber
  inputAmountRaw: string
  inputCurrencyPriceUsd: BigNumber
  /**
   * Price in output currency
   */
  inputCurrencyPrice: BigNumber

  outputAmount: BigNumber
  outputAmountRaw: string
  outputCurrencyPriceUsd: BigNumber
  /**
   * Price in input currency
   */
  outputCurrencyPrice: BigNumber

  priceImpact: number
  type: QuoteType
  fee: FeeData | undefined
  feeConfig: FeeConfig | undefined
  txToSign: string | undefined
}

export interface EvmQuote extends QuoteCommons {
  network: typeof Network.Ethereum | typeof Network.Base
  gasPrices: SuggestedGasSettings.Info
}

export interface SolTonQuote extends QuoteCommons {
  network: typeof Network.Solana | typeof Network.Ton
  networkCostUsd: number
}

export type Quote = EvmQuote | SolTonQuote

export const fromDto = (
  dto: ServicesApi.QuoteDto,
  network: Network.Network,
): Quote => {
  const type = dto.swapType as QuoteType
  const inputAmountRaw = dto.input.amountRaw || dto.input.amount
  const outputAmountRaw = dto.output.amountRaw || dto.output.amount
  const commons: QuoteCommons = {
    priceImpact: dto.priceImpact,
    minimumReceive: BigNumber(dto.output.minOutAmount),

    inputAmount: BigNumber(dto.input.amount),
    outputAmount: BigNumber(dto.output.amount),

    inputCurrencyPrice: BigNumber(dto.input.priceToken),
    outputCurrencyPrice: BigNumber(dto.output.priceToken),

    inputCurrencyPriceUsd: BigNumber(dto.input.priceUsd),
    outputCurrencyPriceUsd: BigNumber(dto.output.priceUsd),
    inputAmountRaw,
    outputAmountRaw,
    fee: dto.fee,
    feeConfig: dto.feeConfig,
    type: type,
    txToSign: dto.swap?.serializedTx,
  }

  if (typeof dto.networkCost === 'number') {
    return {
      ...commons,
      network: network as Exclude<
        Network.Network,
        typeof Network.Ethereum | typeof Network.Base | typeof Network.Bsc
      >,
      networkCostUsd: dto.networkCost,
    }
  }

  return {
    ...commons,
    network: network as typeof Network.Ethereum | typeof Network.Base,
    gasPrices: SuggestedGasSettings.fromDto(dto.networkCost),
  }
}

const SOLANA_AVG_WAIT_TIME = Duration.minutes(1)

export const averageWaitTime = (quote: Quote): Duration.Duration => {
  return match(quote)
    .with({ network: Network.Solana }, () => SOLANA_AVG_WAIT_TIME)
    .with({ network: Network.Ton }, () => SOLANA_AVG_WAIT_TIME)
    .with({ network: P.union(Network.Ethereum, Network.Base) }, (quote) =>
      SuggestedGasSettings.averageWaitTimeEstimate(
        quote.gasPrices.variants.market,
      ),
    )
    .exhaustive()
}

type PathfinderVariantDto = QuoteResponseBody['variants'][number]
const variantEffectivePrice = (variant: PathfinderVariantDto) =>
  variant.effective_price
const _ordVariant = Order.mapInput(Order.Number, variantEffectivePrice)

export const fromPathfinderDto = (
  dto: QuoteResponseBody,
  currencyIn: CurrencyModel,
  currencyOut: CurrencyModel,
): SolTonQuote | undefined => {
  const pickBestVariant = (
    quote: QuoteResponseBody,
  ): QuoteResponseBody['variants'][number] | undefined => {
    if (quote.variants.length === 0) {
      return undefined
    }
    return [...quote.variants].sort((a, b) => {
      if (a.effective_price !== b.effective_price) {
        return a.effective_price - b.effective_price
      }
      return b.total_amount_out - a.total_amount_out
    })[0]
  }

  const variant = pickBestVariant(dto)
  if (!variant) {
    return undefined
  }

  const outputAmount = CurrencyModel.convertBigIntToAmount(currencyOut)(
    variant.total_amount_out,
  )

  return {
    // TODO: Wait for BE
    type: 'EXACT_INPUT',
    // TODO: Wait for BE
    fee: undefined,
    feeConfig: undefined,
    txToSign: variant.transaction ?? undefined,
    // TODO: Wait and check with BE
    minimumReceive: outputAmount,
    inputAmount: CurrencyModel.convertBigIntToAmount(currencyIn)(
      dto.total_amount_in,
    ),
    inputAmountRaw: dto.total_amount_in.toString(),
    outputAmount: outputAmount,
    outputAmountRaw: variant.total_amount_out.toString(),

    // TODO: Wait for BE
    inputCurrencyPrice: BigNumber(0),
    inputCurrencyPriceUsd: BigNumber(0),
    outputCurrencyPriceUsd: BigNumber(0),
    outputCurrencyPrice: BigNumber(variant.effective_price),
    network: Network.Solana,
    // TODO: Wait for BE
    networkCostUsd: 0,
    priceImpact: variant.price_impact_bps / 100,
  }
}

export const COMPUTE_UNIT = 1_000_000
